/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Modal, Tag } from "antd";
import { Link } from 'react-router-dom';
import { serviceService, serviceCategoryService } from '../../../services/serviceService';
import ServicePriceModal from './ServicePriceModal';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatCurrency } from '../../../lib/format';

const ServiceList = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [priceModalService, setPriceModalService] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await serviceCategoryService.getAll({ per_page: 100, is_active: true });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchServices = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await serviceService.getAll({
        page,
        per_page: pageSize,
        search: searchTerm || undefined,
        category_id: categoryFilter || undefined
      });

      setServices((response.data || []).map(s => {
        const particularPrice = (s.activePrices || []).find(p => p.price_type === 'particular' && !p.insurance_company_id);
        return {
          id: s.id,
          name: s.name,
          code: s.code,
          category_name: s.category?.name || 'N/A',
          duration_minutes: s.duration_minutes,
          is_active: s.is_active,
          particular_price: particularPrice ? particularPrice.price : null,
        };
      }));
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching services:', error);
      message.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchServices(pagination.current, pagination.pageSize);
  }, [fetchServices, pagination.current, pagination.pageSize]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchServices(1, pagination.pageSize);
    setPagination({ ...pagination, current: 1 });
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await serviceService.delete(serviceToDelete.id);
      message.success('Servicio eliminado correctamente');
      setDeleteModalVisible(false);
      setServiceToDelete(null);
      fetchServices(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al eliminar servicio';
      message.error(errorMsg);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Link to={`/edit-service?id=${record.id}`}>{text}</Link>
      )
    },
    {
      title: "Código",
      dataIndex: "code",
    },
    {
      title: "Categoría",
      dataIndex: "category_name",
    },
    {
      title: "Duración (min)",
      dataIndex: "duration_minutes",
      render: (val) => val ? `${val} min` : 'N/A'
    },
    {
      title: "Precio Part.",
      dataIndex: "particular_price",
      render: (val) => (
        <span className={val != null ? 'fw-medium' : 'text-muted small'}>{formatCurrency(val)}</span>
      )
    },
    {
      title: "Estado",
      dataIndex: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      )
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to={`/edit-service?id=${record.id}`}>
                <i className="far fa-edit me-2" />
                Editar
              </Link>
              <button
                className="dropdown-item"
                onClick={() => setPriceModalService(record)}
              >
                <i className="fas fa-tag me-2" />
                Gestionar Precios
              </button>
              <button
                className="dropdown-item"
                onClick={() => {
                  setServiceToDelete(record);
                  setDeleteModalVisible(true);
                }}
              >
                <i className="far fa-trash-alt me-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Servicios</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Servicios' }]} />
              </div>
              <div className="col-auto text-end float-end ms-auto">
                <Link to="/add-service" className="btn btn-primary">
                  <i className="fas fa-plus me-2" />
                  Agregar Servicio
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Lista de Servicios</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <form onSubmit={handleSearch}>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <button type="submit" className="btn btn-primary">
                            <i className="fas fa-search" />
                          </button>
                        </div>
                      </form>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={categoryFilter || ''}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value ? parseInt(e.target.value) : null);
                          setPagination({ ...pagination, current: 1 });
                        }}
                      >
                        <option value="">Todas las categorías</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    dataSource={services}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <ServicePriceModal
            service={priceModalService}
            open={Boolean(priceModalService)}
            onClose={() => {
              setPriceModalService(null);
              fetchServices(pagination.current, pagination.pageSize);
            }}
          />

          <Modal
            title="Confirmar Eliminación"
            open={deleteModalVisible}
            onOk={handleDelete}
            onCancel={() => {
              setDeleteModalVisible(false);
              setServiceToDelete(null);
            }}
            okText="Eliminar"
            okButtonProps={{ danger: true }}
          >
            <p>
              ¿Está seguro de eliminar el servicio <strong>{serviceToDelete?.name}</strong>?
            </p>
          </Modal>
    </>
  );
};

export default ServiceList;
